import { useEffect, useState } from 'react'
import {
  createSubResource,
  getLayoutByIdApi,
  getSubResources,
  hideLoader,
  showLoader,
  updateSubResource360v1,
} from '@app-store'

import { isEmpty } from 'lodash'
import {
  Accordion,
  ConfirmDialog,
  CustomButton,
  Modal,
  MultiLineInput,
  SvgPencilEditIcon,
  SvgTrashIcon,
  SvgVerticalSplitter,
} from '@ui-kit'
import { useAppDispatch } from '../utils/agent.store'
import moment from 'moment'
import { editEntityData } from '../apis/entityData.api'
import { EntityLayout } from '../types/layout.types'
import { NotesSectionProps, Record } from '../types/threads.types'
import {
  componentNames,
  MODE_CONSTANTS,
  NAMESPACES,
  ROW_TYPES,
} from '../utils/constants'
import { FieldValues, Path } from 'react-hook-form'
import { AddNotesModal } from './AdditionalNotes'
import {
  isReadOnlyUser,
  currentUserId,
  isProgramAdmin,
  isSuperAdmin,
} from '../utils/permission'

const ThreadsNotesAccordion = <T extends FieldValues>({
  disabled,
  relationIdentifier,
  row,
  layoutData,
  id,
  mode,
  register,
  name,
  createLayoutId,
  entityId,
  entityName,
  tabIndex,
  isSuggestionAddButton,
}: NotesSectionProps<T>) => {
  const [notes, setNotes] = useState<Record[]>([])
  const [newNote, setNewNote] = useState<string>('')
  const [disableButton, setDisableButton] = useState<boolean>(false)
  const [disableButtonEdit, setDisableButtonEdit] = useState<boolean>(false)
  const [editingNote, setEditingNote] = useState<number | null>(null)
  const [editText, setEditText] = useState<string>('')
  const [notesSubResourceData, setNotesSubResourceData] = useState<Record[]>()
  const [threadLayoutData, setThreadLayoutData] = useState<EntityLayout>()
  const [deleteThreadModal, setDeleteThreadModal] = useState<boolean>(false)
  const [deleteElementIndex, setDeleteElementIndex] = useState<number>(0)
  const [openAddModal, setOpenAddModal] = useState<boolean>(false)
  const [notePermissions, setNotePermissions] = useState<{
    [noteId: string]: boolean
  }>({})
  const dispatch = useAppDispatch()

  const getSubResourceData = async (data = threadLayoutData) => {
    try {
      return await getSubResources(
        data?.entity_name ?? '',
        data?.entity_id ? data?.entity_id.toString() : '',
        data?.id ? data?.id.toString() : '',
        relationIdentifier?.toString(),
        false,
        id ? id?.toString() : '',
        undefined,
        undefined,
        undefined,
        [
          {
            criteria: [
              {
                field_name: 'created_at',
                direction: 'DESC',
              },
            ],
          },
        ],
        [
          ...Object.keys(data?.layout_data?.added_fields || {}),
          NAMESPACES.HEADERS,
          NAMESPACES.SOURCE,
          'users_id',
        ]
      )
    } catch (error) {
      console.log(error)
    }
  }

  const fetchDataForViewAndEdit = async () => {
    if (id && createLayoutId) {
      let responseData
      try {
        if (!threadLayoutData) {
          dispatch(showLoader())
          const { data } = await getLayoutByIdApi({
            entityId: row?.lookup_meta?.id?.toString() ?? entityId,
            layoutId:
              layoutData?.layout_data?.added_fields?.[
                row?.lookup_meta?.lookup_keyName ?? ''
              ]?.create_layout_id ??
              createLayoutId ??
              '',
          })
          setThreadLayoutData(data)
          responseData = await getSubResourceData(data)
          dispatch(hideLoader())
        } else {
          responseData = await getSubResourceData()
        }
        const permissions: { [noteId: string]: boolean } = {}
        responseData?.data?.forEach((note: Record) => {
          permissions[note.id] = note.users_id === currentUserId()
        })
        setNotePermissions(permissions)
        setNotes(responseData?.data ?? [])
        setNotesSubResourceData(responseData?.data)
      } catch (error) {
        dispatch(hideLoader())
        console.log(error)
      }
    }
  }

  const addThreadNote = async () => {
    try {
      setDisableButton(true)
      await createSubResource(
        threadLayoutData?.entity_name ?? '',
        threadLayoutData?.entity_id
          ? threadLayoutData?.entity_id.toString()
          : '',
        threadLayoutData?.id ? threadLayoutData?.id.toString() : '',
        id ? id?.toString() : '',
        relationIdentifier?.toString(),
        { name: newNote, users_id: currentUserId() ?? '' }
        // TODO: required in next release
        // undefined,
        // undefined,
        // NAMESPACES?.ADDED
      )
      await fetchDataForViewAndEdit()
      setNewNote('')
    } catch (error) {
      console.log(error)
    } finally {
      setDisableButton(false)
    }
  }
  function capitalizeFirstLetter(str: string) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
  const addNote = () => {
    if (id) {
      addThreadNote()
    }
  }
  const bottomRowValueRenderer = (indexVal: number, topRow?: boolean) => {
    if (
      topRow &&
      !threadLayoutData?.layout_data?.pages[0]?.rows[0]?.columns[1]?.elements
        ?.length
    ) {
      return ''
    }
    const threadAuditData: string[] = []
    threadLayoutData?.layout_data?.pages[0]?.rows[
      topRow ? 0 : 1
    ]?.columns?.forEach((val) => {
      if (
        (topRow && val?.elements?.[0]?.keyName === 'name') ||
        !val?.elements?.length
      )
        return
      const value = val?.elements[0]?.keyName
      const heading = capitalizeFirstLetter(
        (value as string)?.replace('_', ' ')
      )
      if (notes?.[indexVal]?.[value as keyof Record])
        threadAuditData.push(
          `${heading} - ${
            threadLayoutData?.layout_data?.added_fields?.[value as keyof Record]
              ?.ui_data_type === componentNames.DATE_TIME
              ? moment(notes[indexVal]?.[value as keyof Record]).format(
                  'MMM D, YYYY, h:mm A'
                )
              : notes[indexVal]?.[value as keyof Record]
          }`
        )
    })
    return threadAuditData.join(' | ')
  }

  const updateNote = async (id: number) => {
    try {
      setDisableButtonEdit(true)
      await editEntityData(
        threadLayoutData?.entity_name ?? '',
        threadLayoutData?.entity_id ?? 0,
        threadLayoutData?.id ?? 0,
        { name: editText, id: notes[id]?.id },
        notes[id]?.id ? notes[id]?.id?.toString() : ''
      )
      setEditingNote(null)
      setEditText('')
      fetchDataForViewAndEdit()
    } catch (error) {
      console.log(error)
    } finally {
      setDisableButtonEdit(false)
      setDisableButton(false)
    }
  }

  const deleteNote = async (noteId: number) => {
    try {
      updateSubResource360v1(
        layoutData?.entity_id
          ? layoutData?.entity_id?.toString()
          : entityId?.toString() ?? '',
        relationIdentifier?.toString(),
        [
          {
            // _state: 'CHANGED',
            _state: NAMESPACES.REMOVED,
            id: notes[noteId]?.id || '',
            name: notes[noteId]?.name,
            record_status: NAMESPACES.DELETED,
          },
        ],
        id ? id?.toString() : '',
        layoutData?.entity_name ?? entityName ?? '',
        threadLayoutData?.entity_name ?? ''
      )
      setNotes((prev: Record[]) =>
        prev.filter((_: Record, index: number) => index !== noteId)
      )
      setNotePermissions((prev) => {
        const updated = { ...prev }
        delete updated[notes[noteId]?.id]
        return updated
      })
    } catch (error) {
      console.log(error)
    }
  }
  const handleNoteFieldFocus = () => {
    document.getElementById('editing-note')?.focus()
  }
  const getAccordionHeading = () => {
    return (
      <div className="flex justify-between items-center">
        <span>
          {' '}
          {row?.label ??
            threadLayoutData?.layout_data?.pages[0]?.rows?.[0]?.label?.toString() ??
            ROW_TYPES.NOTES}
        </span>
        {isSuggestionAddButton ? (
          <div className="flex gap-4 pr-[0.28rem] items-center">
            <CustomButton
              widthClass="w-[3.25rem]"
              onClick={(e) => {
                e.stopPropagation()
                setOpenAddModal(true)
              }}
              className="!h-7"
              tabIndex={tabIndex}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e?.key === 'Enter' || e?.key === ' ') setOpenAddModal(true)
              }}
            >
              {NAMESPACES.ADD}
            </CustomButton>
            <SvgVerticalSplitter />
          </div>
        ) : null}
      </div>
    )
  }

  const canEditDeleteNote = (note: Record): boolean => {
    return notePermissions[note.id] || isSuperAdmin() || isProgramAdmin()
  }

  useEffect(() => {
    if (!notesSubResourceData?.length && id && mode !== MODE_CONSTANTS.ADD) {
      fetchDataForViewAndEdit()
    }
  }, [id, createLayoutId])

  useEffect(() => {
    if (editingNote !== null)
      document?.getElementById(`editnote-${editingNote}`)?.focus()
  }, [editingNote])
  return (
    <>
      <Accordion
        heading={getAccordionHeading()}
        className={'p-2'}
        isDefaultActive={mode === MODE_CONSTANTS.ADD}
        triggerOpen={!!notesSubResourceData?.length}
        id={row?.rowId}
        tabIndex={tabIndex}
      >
        <div className="w-full p-4">
          <MultiLineInput
            className="w-full border rounded p-4 min-h-20 overflow-y-auto"
            name={name as Path<T>}
            placeholder={NAMESPACES.PLACEHOLDER_THREADS}
            value={mode !== MODE_CONSTANTS.ADD ? newNote : undefined}
            widthClass="w-full"
            onChange={
              mode !== MODE_CONSTANTS.ADD
                ? (e) => setNewNote(e.target.value)
                : undefined
            }
            disabled={isReadOnlyUser() || disabled || disableButton}
            register={mode === MODE_CONSTANTS.ADD ? register : undefined}
            tabIndex={tabIndex}
            id="editing-note"
          />
          {!isEmpty(newNote) && mode !== MODE_CONSTANTS.ADD ? (
            <div className="flex justify-end gap-2 mt-2">
              <CustomButton
                variant="outline"
                onClick={() => setNewNote('')}
                widthClass={'w-[4.3125rem]'}
                className={'h-[1.75rem]'}
                disabled={disableButton}
                tabIndex={tabIndex}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    setNewNote('')
                    handleNoteFieldFocus()
                  }
                }}
              >
                {NAMESPACES.CANCEL_BUTTON}
              </CustomButton>
              <CustomButton
                onClick={addNote}
                widthClass={'w-[3.5rem]'}
                className={'h-[1.75rem]'}
                isFilled={true}
                disabled={disableButton || !newNote?.trim()}
                tabIndex={tabIndex}
              >
                {NAMESPACES.SAVE_BUTTON}
              </CustomButton>
            </div>
          ) : null}
          <div className="max-h-[42rem] overflow-auto">
            {mode !== MODE_CONSTANTS.ADD &&
              notes?.map((note: Record, index) => (
                <div
                  key={note?.id}
                  className={`bg-white shadow-md rounded-lg mt-4 min-h-20`}
                >
                  <div className="p-4 flex flex-col ">
                    {editingNote === index ? (
                      <div className="pb-1">
                        <MultiLineInput
                          name="editNote"
                          className="w-full border rounded p-2 min-h-20 overflow-y-auto"
                          value={editText}
                          widthClass="w-[10rem]"
                          onChange={(e) => setEditText(e.target.value)}
                          disabled={disableButtonEdit}
                          tabIndex={tabIndex}
                          id={`editnote-${index}`}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <CustomButton
                            widthClass={'w-[4.3125rem]'}
                            className={'h-[1.75rem]'}
                            onClick={() => {
                              setDisableButton(false)
                              setEditingNote(null)
                            }}
                            disabled={disableButtonEdit}
                            tabIndex={tabIndex}
                            onKeyDown={(e) => {
                              if (e?.key === 'Enter' || e?.key === ' ') {
                                setDisableButton(false)
                                setEditingNote(null)
                                handleNoteFieldFocus()
                              }
                            }}
                          >
                            {NAMESPACES.CANCEL_BUTTON}
                          </CustomButton>
                          <CustomButton
                            widthClass={'w-[3.5rem]'}
                            className={'h-[1.75rem]'}
                            onClick={() => updateNote(index)}
                            isFilled={true}
                            disabled={disableButtonEdit || !editText?.trim()}
                            tabIndex={tabIndex}
                            onKeyDown={async (e) => {
                              if (e?.key === 'Enter' || e?.key === ' ') {
                                await updateNote(index)
                                handleNoteFieldFocus()
                              }
                            }}
                          >
                            {NAMESPACES.SAVE_BUTTON}
                          </CustomButton>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <div className="flex flex-col w-full">
                          {!isEmpty(note?.headers) ? (
                            <div className="w-full mb-1 pb-2">
                              {note?.headers}
                            </div>
                          ) : null}

                          <p className="text-gray-700 text-xs leading-tight w-full pr-2 min-h-8 max-h-[10rem] overflow-y-auto">
                            {note.name?.replace(/([a-z]{5})/gi, '$1\u00AD')}
                          </p>
                        </div>

                        <div className="flex flex-col justify-between items-end pr-3">
                          <div className="text-Geologica font-light text-xs text-gray-500">
                            {bottomRowValueRenderer(index, true)}
                          </div>
                        </div>
                      </div>
                    )}
                    {
                      <div className="text-Geologica font-light text-xs text-gray-500  pt-4">
                        {bottomRowValueRenderer(index)}
                      </div>
                    }
                    {canEditDeleteNote(note) && editingNote !== index ? (
                      <div className="flex justify-end gap-2 pr-2 pt-2">
                        {!disableButton && !isReadOnlyUser() ? (
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => {
                              setDisableButton(true)
                              setEditingNote(index)
                              setEditText(note.name)
                            }}
                            tabIndex={tabIndex}
                            onKeyDown={(e) => {
                              if (e?.key === 'Enter' || e?.key === ' ') {
                                e?.preventDefault()
                                setDisableButton(true)
                                setEditingNote(index)
                                setEditText(note.name)
                              }
                            }}
                          >
                            <SvgPencilEditIcon />
                          </button>
                        ) : null}
                        {!disableButton && !isReadOnlyUser() ? (
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => {
                              setDeleteThreadModal(true)
                              setDeleteElementIndex(index)
                            }}
                            tabIndex={tabIndex}
                            onKeyDown={(e) => {
                              if (e?.key === 'Enter' || e?.key === ' ') {
                                e?.preventDefault()
                                setDeleteThreadModal(true)
                                setDeleteElementIndex(index)
                                handleNoteFieldFocus()
                              }
                            }}
                          >
                            <SvgTrashIcon />
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Accordion>
      <Modal
        isOpen={openAddModal}
        closeModal={() => setOpenAddModal(false)}
        bodyCssClassName="min-w-[62rem]"
        showCloseButton={false}
      >
        <AddNotesModal
          onSave={(val) => {
            setNewNote(val)
            setOpenAddModal(false)
          }}
          onClose={() => setOpenAddModal(false)}
        />
      </Modal>
      {deleteThreadModal && (
        <ConfirmDialog
          message={NAMESPACES.CANCEL_THREAD_MSG}
          onConfirm={() => {
            setDeleteThreadModal(false)
            deleteNote(deleteElementIndex)
          }}
          onCancel={() => {
            setDeleteThreadModal(false)
          }}
          open={deleteThreadModal}
        />
      )}
    </>
  )
}

export default ThreadsNotesAccordion
